import { CreateResponse, DeleteResponse, SAFE_LIMIT, UpdateResponse } from '.';
import { clearAppData } from '../app-data';
import { JsonObject } from '../helpers';
import { AgentMeta, AgentModel } from '../models/agent.model';
import { PolicyMeta } from '../models/policy-model-v1';
import { UserEventModel } from '../models/user-event/user-event.model';
import { AppDataQueryKey } from '../query-client';
import { getPoliciesByAgentIdsV1 } from './policy-v1.service';
import { deleteApi, get, post, update } from './sp-api.service';

export type AgentUsage = { policies: PolicyMeta[]; inUse: boolean };

export const createAgent = async (agent: AgentModel): Promise<CreateResponse<AgentModel>> => {
  const response = await post('/shortcut/assistant', agent._props);

  if (!response?.insertedId || !response?.assistant) {
    return { created: false, error: 'Unable to create assistant' };
  }

  clearAppData(AppDataQueryKey.Agents);

  return {
    created: true,
    model: new AgentModel({ ...(response.assistant as JsonObject) }),
  };
};

export const updateAgent = async (agent: AgentModel): Promise<UpdateResponse> => {
  const response = await update(`/shortcut/assistant/${agent.id}`, agent._props);

  if (response.updated) {
    clearAppData(AppDataQueryKey.Agents);
  }

  return response;
};

export const deleteAgent = async (agentId: string): Promise<DeleteResponse> => {
  const response = (await deleteApi(`/shortcut/assistant/${agentId}`)) as JsonObject;

  if (response?.deleted) {
    clearAppData(AppDataQueryKey.Agents);
    return { deleted: true };
  }

  return { deleted: false, error: 'Unable to delete assistant' };
};

export const getAgentById = async (agentId: string): Promise<AgentModel | null> => {
  const response = (await get('/shortcut/assistant', { assistantIds: agentId })) as {
    assistants: JsonObject[];
  };
  return response?.assistants?.length ? new AgentModel(response.assistants[0]) : null;
};

export const getAllAgents = async (): Promise<AgentModel[]> => {
  const { assistants } = (await get('/shortcut/assistant')) as {
    assistants: JsonObject[];
  };

  if (!Array.isArray(assistants)) {
    return [];
  }

  return assistants.map((data) => new AgentModel(data));
};

export const getAgentsByIds = async (agentIds: string[]): Promise<AgentModel[]> => {
  return getAllAgents().then((agents) => agents.filter(({ id }) => agentIds.includes(id)));
};

export const getAgentsByModelIds = async (modelIds: string[]): Promise<AgentModel[]> => {
  return getAllAgents().then((agents) =>
    agents.filter(({ privateModelId }) => modelIds.includes(privateModelId))
  );
};

export const getAgentsByDatasourceIds = async (datasourceIds: string[]): Promise<AgentModel[]> => {
  return getAllAgents().then((agents) =>
    agents.filter(({ dataSourceIds: agentDatasourceIds }) =>
      agentDatasourceIds.some((dsId) => datasourceIds.includes(dsId))
    )
  );
};

export const getAgentUsage = async (agentIds: string[]): Promise<Map<string, AgentUsage>> => {
  const policies = await getPoliciesByAgentIdsV1(agentIds);

  const usageMap: Map<string, AgentUsage> = new Map();
  agentIds.forEach((agentId) => {
    const usage = {
      policies: policies
        .filter(({ assistants }) => assistants.enabled.includes(agentId))
        .map(({ meta }) => meta),
    };
    usageMap.set(agentId, { ...usage, inUse: !!usage.policies.length });
  });

  return usageMap;
};

export const getAgentsAppData = async (): Promise<AgentMeta[]> => {
  const fields = ['name', 'image', 'portal'];

  const { assistants } = (await get('/shortcut/assistant', {
    page: 1,
    pageSize: SAFE_LIMIT,
    fields,
  })) as {
    assistants: JsonObject[];
  };

  if (!Array.isArray(assistants)) {
    return [];
  }

  return assistants.map((data) => new AgentModel(data).meta);
};

export const getUserEventAgent = (event: UserEventModel, agents: AgentMeta[]): AgentMeta | null => {
  const { assistant } = event || {};
  const { id: assistantId } = assistant || {};
  if (!assistantId) {
    return null;
  }
  return agents.find(({ id }) => id === assistantId) || null;
};
